import { useId, useRef } from 'react';

import { newBlockId } from '@features/editorial/_shared/body';
import type { BlockTree } from '@shared/block-renderer';
import { formatNumber } from '@shared/format';
import { ArrowDownIcon, ArrowUpIcon, TrashIcon } from '@shared/ui/icons';
import { PlusIcon } from '@shared/ui/icons/nav';

import {
  blockText,
  createPageBlock,
  PAGE_BLOCK_TYPES,
  PAGE_BLOCKS,
  withText,
  type PageBlockType,
} from '../_shared/blocks';
import { TEXTAREA_CLASS, CONTROL_CLASS } from '../_shared/components';

import { copy } from './copy';

/**
 * The informational body's editor — **a field per block**, not a flow.
 *
 * The difference from the editorial post is volume, not taste. A page body is a
 * handful of blocks edited occasionally, and discrete labelled controls are the
 * most robust and the most accessible way to hold them: a real label bound by
 * `for`, a real control, nothing to go wrong. An article is dozens of blocks
 * written in one sitting, where a field per block costs a `Tab` before every
 * paragraph — which is why that surface, and only that surface, carries the
 * continuous region.
 *
 * **The block is the group, so the control inside it is bare.** Its kind is
 * visible in the badge and announced through a visually-hidden `<label>`; what
 * it needs from a field component is the full width and the separated hint, not
 * the 30 rem cap of a field filled with one line.
 *
 * **The move handles are disabled at the ends, never hidden.** Hiding one jumps
 * the head's width between blocks and pulls a focus target out from under the
 * keyboard mid-use.
 */
export function AboutBlockEditor({
  blocks,
  onChange,
  announce,
  noteAfterBlocks,
}: {
  blocks: BlockTree;
  onChange: (next: BlockTree) => void;
  announce: (message: string) => void;
  /**
   * The inline-layer note, rendered **between the list and the add zone** —
   * where the pack puts it. It is a statement about the blocks above it, and
   * below the add zone it would read as a caveat on the insertion instead.
   */
  noteAfterBlocks: string;
}) {
  const fieldPrefix = useId();
  const container = useRef<HTMLOListElement>(null);
  const addZone = useRef<HTMLDivElement>(null);

  function focusIn(selector: string, index: number) {
    // The DOM has not been updated yet when a handler runs, so the move is
    // queued behind the render that produces the node it lands on.
    queueMicrotask(() => {
      const rows = container.current?.children;
      const row = rows?.[index] as HTMLElement | undefined;
      const target = row?.querySelector<HTMLElement>(selector);
      (target ?? addZone.current?.querySelector<HTMLElement>('button'))?.focus();
    });
  }

  function add(type: PageBlockType) {
    onChange([...blocks, createPageBlock(type, newBlockId())]);
    // Focus lands in the new control: the block was added to be filled, and
    // leaving focus on the button makes the addition look like it did nothing.
    focusIn('[data-block-control]', blocks.length);
    announce(copy.announce.added(PAGE_BLOCKS[type].label));
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= blocks.length) {
      // The end of a list is announced, not silently ignored.
      announce(direction === -1 ? copy.announce.atStart : copy.announce.atEnd);
      return;
    }

    const next = [...blocks];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(target, 0, moved);
    onChange(next);

    // Focus returns to the handle that moved; at an end that handle is now
    // disabled, so its twin takes it rather than dropping the reader to <body>.
    const stillEnabled = direction === -1 ? target > 0 : target < blocks.length - 1;
    focusIn(stillEnabled ? `[data-move="${direction}"]` : `[data-move="${-direction}"]`, target);

    const label = PAGE_BLOCKS[moved.type as PageBlockType]?.label ?? '';
    announce(copy.announce.moved(label, formatNumber(target + 1), formatNumber(blocks.length)));
  }

  function remove(index: number) {
    const doomed = blocks[index];
    if (!doomed) return;

    const next = blocks.filter((_, position) => position !== index);
    onChange(next);

    // Focus moves before the node leaves: removing the element that held it
    // drops the reader to the top of the document.
    focusIn('[data-block-remove]', Math.min(index, next.length - 1));

    const label = PAGE_BLOCKS[doomed.type as PageBlockType]?.label ?? '';
    announce(copy.announce.removed(label, formatNumber(next.length)));
  }

  return (
    <>
      <ol ref={container} className="m-0 mb-4 grid list-none gap-3 p-0">
        {blocks.map((block, index) => {
          const spec = PAGE_BLOCKS[block.type as PageBlockType];
          if (!spec) return null;

          const fieldId = `${fieldPrefix}-${block.id}`;
          const hintId = `${fieldId}-hint`;

          return (
            <li key={block.id} className="rounded-md border border-border bg-surface p-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="rounded-xs border border-border-strong px-2 py-1 text-xs text-ink-muted">
                  {spec.label}
                </span>

                <span className="ms-auto flex gap-1">
                  <button
                    type="button"
                    data-move="-1"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    aria-label={copy.block.moveUp}
                    className="flex size-8 items-center justify-center rounded-sm text-ink-muted hover:bg-hover-bg hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <ArrowUpIcon />
                  </button>
                  <button
                    type="button"
                    data-move="1"
                    disabled={index === blocks.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label={copy.block.moveDown}
                    className="flex size-8 items-center justify-center rounded-sm text-ink-muted hover:bg-hover-bg hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <ArrowDownIcon />
                  </button>
                  {/*
                    Danger on the one head action that loses text an operator
                    wrote — and colour is not the only signal: the button names
                    the kind it removes.
                  */}
                  <button
                    type="button"
                    data-block-remove
                    onClick={() => remove(index)}
                    aria-label={copy.block.remove(spec.label)}
                    className="flex size-8 items-center justify-center rounded-sm text-danger hover:bg-danger-soft"
                  >
                    <TrashIcon />
                  </button>
                </span>
              </div>

              <label className="sr-only" htmlFor={fieldId}>
                {spec.fieldLabel}
              </label>

              {spec.multiline ? (
                <textarea
                  id={fieldId}
                  data-block-control
                  rows={4}
                  value={blockText(block)}
                  aria-describedby={spec.hint ? hintId : undefined}
                  onChange={(event) =>
                    onChange(
                      blocks.map((row, position) =>
                        position === index ? withText(row, event.target.value) : row,
                      ),
                    )
                  }
                  className={TEXTAREA_CLASS}
                />
              ) : (
                <input
                  id={fieldId}
                  data-block-control
                  type="text"
                  value={blockText(block)}
                  onChange={(event) =>
                    onChange(
                      blocks.map((row, position) =>
                        position === index ? withText(row, event.target.value) : row,
                      ),
                    )
                  }
                  className={CONTROL_CLASS}
                />
              )}

              {spec.hint ? (
                <p id={hintId} className="mb-0 mt-2 text-xs text-ink-muted">
                  {spec.hint}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      {blocks.length === 0 ? <p className="mb-4 mt-0 text-sm text-ink-muted">{copy.body.empty}</p> : null}

      <p className="mb-0 mt-0 text-xs text-ink-muted">{noteAfterBlocks}</p>

      {/*
        The insertion zone is a bounded panel, not a naked row of small buttons:
        an editor where «how do I add» is not visible is an incomplete editor
        however correct its markup. Dashed border because it is an empty position
        waiting rather than a content card — a solid one would give it the weight
        of the blocks above and blur the two — over the sunken ground, so filled
        against empty reads from elevation alone with no fifth colour.
      */}
      <div
        ref={addZone}
        role="group"
        aria-label={copy.add.label}
        className="mb-6 mt-4 grid gap-4 rounded-md border border-dashed border-border-strong bg-bg-sunken p-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center md:gap-5"
      >
        <div className="min-w-0">
          <p className="mb-1 font-display text-h4 font-semibold">{copy.add.title}</p>
          <p className="mb-0 text-xs text-ink-muted">{copy.add.hint}</p>
        </div>

        {/*
          Three equal columns at the base tier: stacking three one-word actions
          would spend three 2.75rem rows on one decision, and a horizontally
          scrolled strip would hide a kind from anyone who does not know it is
          there. Full-size buttons, not `--sm` — adding a block is a primary act
          of this surface and the target holds at every tier.
        */}
        <div className="grid grid-cols-3 gap-2 md:flex md:flex-wrap">
          {PAGE_BLOCK_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => add(type)}
              className="hit-target flex w-full items-center justify-center gap-2 rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay md:w-auto"
            >
              <PlusIcon />
              <span>{PAGE_BLOCKS[type].label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
